import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEditorialTitle } from './titleContext'
import {
  actionable, busy, checkWpNow, dismissWpPending, importWpPending, listWpPending, publishable, publishedText, publishWpPending,
  queuedText, restoreWpPending, rowStatusText, whatText,
  type WpPendingEntry, type WpPendingFilters,
} from './wpPending'

const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow'
const btn = `inline-flex min-h-9 items-center justify-center whitespace-nowrap rounded-lg border border-ink-400 px-3 font-ui text-[13px] font-semibold text-paper hover:bg-ink-600 disabled:opacity-50 ${focus}`
const select = `min-h-9 rounded-lg border border-ink-400 bg-ink-800 px-2 font-ui text-[13px] text-paper ${focus}`
const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : '')

/** From WordPress: posts written or changed in WordPress that the new site doesn't have yet. Nothing comes over until an editor
 * imports it, and a new post lands unpublished until an editor presses Approve & publish here. */
export function FromWordPressPage() {
  const { publicationSlug: slug } = useEditorialTitle()
  const qc = useQueryClient()
  const [filters, setFilters] = useState<WpPendingFilters>({ status: 'open', kind: '', site: '' })
  const [picked, setPicked] = useState<Set<number>>(new Set())
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [working, setWorking] = useState(false)
  const key = ['editorial-wp-pending', slug, filters]
  const list = useQuery({
    queryKey: key, queryFn: () => listWpPending(slug, filters), retry: false,
    refetchInterval: (q) => (busy(q.state.data?.items) ? 10_000 : false),
  })
  const items = list.data?.items ?? []
  const choosable = items.filter(actionable)
  const waiting = items.filter(publishable)
  const selectable = items.filter((e) => actionable(e) || publishable(e))
  const chosen = choosable.filter((e) => picked.has(e.id))
  const chosenToPublish = waiting.filter((e) => picked.has(e.id))
  const allPicked = selectable.length > 0 && selectable.every((e) => picked.has(e.id))

  const run = async (what: () => Promise<string>) => {
    setWorking(true); setError(null); setMessage(null)
    try {
      setMessage(await what())
      setPicked(new Set())
      await qc.invalidateQueries({ queryKey: ['editorial-wp-pending', slug] })
    } catch (e) {
      const detail = (e as { detail?: unknown }).detail
      setError(typeof detail === 'string' ? detail : 'That didn’t work. Try again.')
    } finally {
      setWorking(false)
    }
  }
  const importIds = (ids: number[], confirm = false) => run(async () => queuedText(await importWpPending(slug, { ids }, filters, confirm)))
  const importAll = () => {
    if (!window.confirm(`Import all ${choosable.length} posts shown? New posts land unpublished, for you to Approve & publish here.`)) return
    void run(async () => queuedText(await importWpPending(slug, { all: true }, filters)))
  }
  const publishIds = (ids: number[]) => run(async () => publishedText(await publishWpPending(slug, { ids }, filters)))
  const publishAll = () => {
    if (!window.confirm(`Approve and publish all ${waiting.length} imported posts shown? Each goes live on the site now.`)) return
    void run(async () => publishedText(await publishWpPending(slug, { all: true }, filters)))
  }
  const dismiss = (ids: number[]) => run(async () => { const r = await dismissWpPending(slug, ids); return `${r.dismissed} left off the list. A later change in WordPress brings one back.` })
  const restore = (ids: number[]) => run(async () => { const r = await restoreWpPending(slug, ids); return `${r.restored} back on the list.` })
  const check = () => run(async () => {
    const r = await checkWpNow(slug)
    const found = (r.new ?? 0) + (r.edited ?? 0) + (r.reopened ?? 0)
    return found ? `Found ${found} more.` : 'Nothing new in WordPress.'
  })
  const toggle = (id: number) => setPicked((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n })
  const setF = (patch: Partial<WpPendingFilters>) => { setFilters((f) => ({ ...f, ...patch })); setPicked(new Set()) }

  return (
    <div className="flex flex-col gap-5">
      <h1 className="m-0 font-serif text-[34px] font-medium leading-tight text-paper sm:text-[40px]">From WordPress</h1>
      <p className="m-0 max-w-3xl font-ui text-[14px] text-paper-dim">
        Posts written or changed in WordPress since {list.data?.since ?? 'the move'} that the new site doesn’t have yet.
        Nothing comes over on its own: import the ones you want, and leave the rest off the list.
        A new post comes over unpublished; read it, then press Approve &amp; publish to put it live.
        {list.data && !list.data.scan_on && !list.data.auto_on && ' The daily check is off, so use Check WordPress now.'}
      </p>
      {list.data?.auto_on && (
        <p role="note" className="m-0 max-w-3xl rounded-lg border border-amber-glow px-3 py-2 font-ui text-[14px] text-paper">
          Automatic publishing is on for this title (Settings → Switches). WordPress is checked every 15 minutes, and new and edited posts
          come over and go live by themselves. Only a piece edited in the desk, or one that couldn’t come over, waits here for you.
        </p>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 font-ui text-[12px] text-paper-dim">Show
          <select className={select} value={filters.status} onChange={(e) => setF({ status: e.target.value as WpPendingFilters['status'] })}>
            <option value="open">To deal with</option><option value="imported">Imported</option>
            <option value="dismissed">Left off</option><option value="all">Everything</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 font-ui text-[12px] text-paper-dim">Kind
          <select className={select} value={filters.kind} onChange={(e) => setF({ kind: e.target.value as WpPendingFilters['kind'] })}>
            <option value="">New and changed</option><option value="new">New</option><option value="edited">Changed in WordPress</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 font-ui text-[12px] text-paper-dim">Site
          <select className={select} value={filters.site} onChange={(e) => setF({ site: e.target.value as WpPendingFilters['site'] })}>
            <option value="">Free and members'</option><option value="main">Free site</option><option value="membership">Members' site</option>
          </select>
        </label>
        <div className="ml-auto flex flex-wrap gap-2">
          <button type="button" className={btn} disabled={working} onClick={check}>Check WordPress now</button>
          {filters.status !== 'dismissed' && <>
            <button type="button" className={btn} disabled={working || chosen.length === 0} onClick={() => importIds(chosen.map((e) => e.id))}>
              Import selected{chosen.length ? ` (${chosen.length})` : ''}
            </button>
            <button type="button" className={btn} disabled={working || choosable.length === 0} onClick={importAll}>Import all shown</button>
            {(waiting.length > 0 || chosenToPublish.length > 0) && <>
              <button type="button" className={btn} disabled={working || chosenToPublish.length === 0} onClick={() => publishIds(chosenToPublish.map((e) => e.id))}>
                Approve &amp; publish selected{chosenToPublish.length ? ` (${chosenToPublish.length})` : ''}
              </button>
              <button type="button" className={btn} disabled={working || waiting.length === 0} onClick={publishAll}>Approve &amp; publish all shown</button>
            </>}
            <button type="button" className={btn} disabled={working || chosen.length === 0} onClick={() => dismiss(chosen.map((e) => e.id))}>Leave off</button>
          </>}
        </div>
      </div>

      {message && <p role="status" className="m-0 font-ui text-[14px] text-paper">{message}</p>}
      {error && <p role="alert" className="m-0 font-ui text-[14px] text-red-400">{error}</p>}

      {list.isLoading ? <p className="m-0 font-ui text-[14px] text-paper-dim">Loading…</p>
        : list.isError ? <p role="alert" className="m-0 font-ui text-[14px] text-red-400">The list couldn’t be loaded.</p>
        : items.length === 0 ? <p className="m-0 font-ui text-[14px] text-paper-dim">Nothing here.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left font-ui text-[14px] text-paper">
              <thead><tr className="text-[12px] uppercase text-paper-dim">
                <th className="p-2">
                  <input type="checkbox" aria-label="Select all shown" disabled={selectable.length === 0}
                    checked={allPicked}
                    onChange={(e) => setPicked(e.target.checked ? new Set(selectable.map((x) => x.id)) : new Set())} />
                </th>
                <th className="p-2">Post</th><th className="p-2">What</th><th className="p-2">Changed in WordPress</th><th className="p-2">Status</th><th className="p-2"><span className="sr-only">Actions</span></th>
              </tr></thead>
              <tbody>{items.map((e) => <Row key={e.id} e={e} picked={picked.has(e.id)} working={working} onToggle={toggle}
                onImport={(confirm) => importIds([e.id], confirm)} onPublish={() => publishIds([e.id])}
                onDismiss={() => dismiss([e.id])} onRestore={() => restore([e.id])} />)}</tbody>
            </table>
          </div>
        )}
    </div>
  )
}

function Row({ e, picked, working, onToggle, onImport, onPublish, onDismiss, onRestore }: {
  e: WpPendingEntry; picked: boolean; working: boolean; onToggle: (id: number) => void
  onImport: (confirm: boolean) => void; onPublish: () => void; onDismiss: () => void; onRestore: () => void
}) {
  const can = actionable(e)
  const toPublish = publishable(e)
  return (
    <tr className="border-t border-ink-500 align-top">
      <td className="p-2">{(can || toPublish) && <input type="checkbox" aria-label={`Select ${e.title}`} checked={picked} onChange={() => onToggle(e.id)} />}</td>
      <td className="p-2">
        <div className="font-semibold">{e.title}</div>
        <div className="flex flex-wrap gap-3 text-[13px]">
          {e.wp_url && <a href={e.wp_url} target="_blank" rel="noreferrer" className="text-amber-glow underline underline-offset-2">In WordPress</a>}
          {e.article_url && <a href={e.article_url} target="_blank" rel="noreferrer" className="text-amber-glow underline underline-offset-2">On the new site</a>}
        </div>
        {e.note && <div className="mt-1 text-[13px] text-paper-dim">{e.note}</div>}
        {e.desk_edited && can && <div className="mt-1 text-[13px] text-amber-glow">Edited in the desk since it came over. Importing replaces the desk’s version.</div>}
      </td>
      <td className="p-2 text-[13px]">{whatText(e)}</td>
      <td className="p-2 text-[13px]">{fmtDate(e.wp_post_modified)}</td>
      <td className="p-2 text-[13px]">
        {rowStatusText(e)}
        {e.error && <div role="alert" className="mt-1 max-w-xs text-red-400">{e.error}</div>}
      </td>
      <td className="p-2">
        <div className="flex flex-wrap gap-2">
          {can && (e.desk_edited
            ? <button type="button" className={btn} disabled={working} onClick={() => { if (window.confirm(`Replace the desk's version of “${e.title}” with WordPress's?`)) onImport(true) }}>Replace the desk’s version</button>
            : <button type="button" className={btn} disabled={working} onClick={() => onImport(false)}>{e.status === 'failed' ? 'Try again' : 'Import'}</button>)}
          {toPublish && <button type="button" className={btn} disabled={working} onClick={onPublish}>Approve &amp; publish</button>}
          {can && <button type="button" className={btn} disabled={working} onClick={onDismiss}>Leave off</button>}
          {e.status === 'dismissed' && <button type="button" className={btn} disabled={working} onClick={onRestore}>Put back</button>}
        </div>
      </td>
    </tr>
  )
}
